import { useCallback, useEffect, useMemo, useState } from "react";
import {
  useTheme,
  Grid,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Badge,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import { isEqual } from "lodash-es";

import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import AddIcon from "@mui/icons-material/Add";

import { IOPortValue, IOSettingsValue, TypeOption, ZoneBehaviorItem, ZoneTriggers } from "./types";
import TitledCard from "../TitledCard";
import { formatMac, valueEdited } from "../utils";
import MaskedTextField from "../MaskedTextField";
import useEditedStyles, { useEditedColor } from "../../../../hooks/useEditedStyles";
import IOPort, { getDefaultIOZoneValue } from "./IOPort";

import { ZoneBehaviors } from "./types";
import { theme } from "@mb-pro-ui/components";
import { ReactComponent as H24Logo } from "../../../../icons/h24.svg";
import ScheduleIcon from "@mui/icons-material/Schedule";
import HistoryToggleOffIcon from "@mui/icons-material/HistoryToggleOff";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import PanToolIcon from "@mui/icons-material/PanTool";
import ZonesOverview from "./ZoneOverview";

import inputPort1 from "./ports/01.svg";
import inputPort2 from "./ports/02.svg";
import inputPort3 from "./ports/03.svg";
import inputPort4 from "./ports/04.svg";
import inputPort5 from "./ports/05.svg";
import inputPort6 from "./ports/06.svg";
import inputPort7 from "./ports/07.svg";
import inputPort8 from "./ports/08.svg";
import inputPort9 from "./ports/09.svg";
import { ResetAdornment, useResetContextMenu } from "../../../../utils/ResetContextMenuProvider";

interface SmartalarmBoardProps {
  index: number;
  value: IOSettingsValue;
  saved: IOSettingsValue | null;
  disabled?: boolean;
  onChange: (index: number, value: IOSettingsValue) => void;
}

export const behaviors: ZoneBehaviorItem[] = [
  {
    value: ZoneBehaviors.IMMEDIATE.toString(2),
    label: <FormattedMessage defaultMessage={"Immediate"} />,
    color: theme.palette.secondary.main,
    icon: <PlayArrowIcon />,
    order: 2,
  },
  {
    value: ZoneBehaviors.DELAYED.toString(2),
    label: <FormattedMessage defaultMessage={"Delayed"} />,
    color: theme.palette.warning.light,
    icon: <ScheduleIcon />,
    order: 0,
  },
  {
    value: ZoneBehaviors.FOLLOWER.toString(2),
    label: <FormattedMessage defaultMessage={"Follower"} />,
    color: theme.palette.warning.dark,
    icon: <HistoryToggleOffIcon />,
    order: 1,
  },
  {
    value: ZoneBehaviors.H24.toString(2),
    label: <FormattedMessage defaultMessage={"24-hour"} />,
    color: theme.palette.error.light,
    icon: <H24Logo />,
    order: 3,
  },
];

export const triggers = [
  { value: ZoneTriggers.arm, label: <FormattedMessage defaultMessage={"Arm"} />, color: theme.palette.error.light, icon: <RadioButtonCheckedIcon /> },
  { value: ZoneTriggers.disarm, label: <FormattedMessage defaultMessage={"Disarm"} />, color: theme.palette.info.main, icon: <RadioButtonUncheckedIcon /> },
  { value: ZoneTriggers.alarm, label: <FormattedMessage defaultMessage={"Alarm"} />, color: theme.palette.error.main, icon: <NotificationsNoneIcon /> },
  { value: ZoneTriggers.manual, label: <FormattedMessage defaultMessage={"Manual"} />, color: theme.palette.common.black, icon: <PanToolIcon /> },
];

export const portTypes: TypeOption[] = [
  { value: "IN1", label: "NC", image: inputPort1 },
  { value: "IN2", label: "NC + EOL", image: inputPort2, r: true },
  { value: "IN3", label: "NO + EOL", image: inputPort3, r: true },
  { value: "IN4", label: "(NC + EOL) + TAMPER", image: inputPort4, r: true },
  { value: "IN5", label: "(NC + EOL) + (TAMPER + EOL)", image: inputPort5, r: true },
  { value: "IN6", label: "(NO + DEOL) + (TAMPER + EOL)", image: inputPort6, r: true },
  { value: "IN7", label: "(2 x NC + DEOL)", image: inputPort7, double: true, r: true },
  { value: "IN8", label: "(2 x NC)  + (2x TAMPER)", image: inputPort8, double: true, r: true },
  { value: "IN9", label: "(2 x NC)  + (2x TAMPER)  + EOL", image: inputPort9, double: true, r: true },
  { value: "OUT", label: <FormattedMessage defaultMessage="Output" /> },
];

const SmartalarmBoard = ({ index: boardIndex, value, saved, disabled, onChange }: SmartalarmBoardProps) => {
  const [protocol, setProtocol] = useState<"C" | "L" | "">("");
  const [mac, setMac] = useState("");
  const [name, setName] = useState("");
  const [portNrs, setPortNrs] = useState<number[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newPortNr, setNewPortNr] = useState("");
  const [omittedNrs, setOmittedNrs] = useState<number[]>([]);
  const [validNewPortNr, setValidNewPortNr] = useState(true);
  const [newPortNrError, setNewPortNrError] = useState<string | null>(null);
  const [newItem, setNewItem] = useState<boolean | null>(null);

  const { formatMessage } = useIntl();
  const theme = useTheme();

  const editedStyle = useEditedStyles();
  const { background: editedBackground, text: editedText } = useEditedColor();
  const { openContextMenu } = useResetContextMenu();

  const handleProtocolChange = useCallback(
    (e) => {
      const protocol = e.target.value;
      setProtocol(protocol);
      value.protocol = protocol === "" ? null : protocol;

      onChange(boardIndex, value);
    },
    [boardIndex, value, onChange]
  );

  const onDelete = useCallback(() => {
    value.deleted = true;
    onChange(boardIndex, value);
  }, [boardIndex, value, onChange]);

  const onRestore = useCallback(() => {
    delete value.deleted;
    onChange(boardIndex, value);
  }, [boardIndex, value, onChange]);

  const openNewPortDialog = useCallback((portNrs: number[]) => {
    const max = portNrs.length === 0 ? 0 : [...portNrs].sort((a, b) => b - a)[0];
    setNewPortNr(`${max + 1}`);
    setOmittedNrs(
      Array.from({ length: max + 1 })
        .map((_, i) => (i === 0 || portNrs.includes(i) ? -1 : i))
        .filter((nr) => nr !== -1)
    );
    setDialogOpen(true);
  }, []);

  const handleNewPortNrChange = useCallback(
    (value: string, portNrs: number[]) => {
      const selected = parseInt(value, 10);
      if (selected === 0 || isNaN(selected)) {
        setNewPortNrError(formatMessage({ defaultMessage: "Invalid IO port number." }));
        setValidNewPortNr(false);
      } else if (portNrs.includes(selected)) {
        setNewPortNrError(formatMessage({ defaultMessage: "You have already configured this IO port." }));
        setValidNewPortNr(false);
      } else {
        setNewPortNrError(null);
        setValidNewPortNr(true);
      }
      setNewPortNr(value);
    },
    [formatMessage]
  );

  const addNewPort = useCallback(
    (nr: number) => {
      if (!value.ports) {
        value.ports = [];
      }
      const smallers = value.ports
        .map((c, i) => ({ nr: c.nr, index: i }))
        .filter((c) => c.nr < nr)
        .sort((a, b) => b.nr - a.nr);
      const newItemIndex = smallers.length > 0 ? smallers[0].index + 1 : 0;
      const { type, resistor, maxZones } = value?.ports?.length > 0 ? value.ports[0].value : { type: "IN1", resistor: null, maxZones: 1 };
      const defaultValue = { type, resistor, zoneValues: Array.from({ length: maxZones }).map(() => getDefaultIOZoneValue()), maxZones };
      value.ports.splice(newItemIndex, 0, { nr, value: defaultValue });
      setPortNrs(value.ports.map((c) => c.nr));
      onChange(boardIndex, value);
    },
    [value, boardIndex, onChange]
  );

  const portValueChanged = useCallback(
    (nr: number, v: IOPortValue) => {
      const i = value.ports ? value.ports.findIndex((c) => c.nr === nr) : -1;
      if (i === -1) {
        return;
      }
      if (value.ports) {
        value.ports[i].value = v;
        onChange(boardIndex, value);
      }
    },
    [boardIndex, value, onChange]
  );

  // const portValueReset = useCallback(
  //   (nr: number, path: string) => {
  //     const i = value.ports ? value.ports.findIndex((c) => c.nr === nr) : -1;
  //     if (i === -1) {
  //       return;
  //     }

  //     if (value.ports) {
  //       const savedValue = saved ? get(saved, path) : {};
  //       set(value.ports[i], path, savedValue);
  //       onChange(boardIndex, value);
  //     }
  //   },
  //   [boardIndex, onChange, saved, value]
  // );

  useEffect(() => {
    setNewItem((old) => {
      let newValue;
      if (value.newItem) {
        delete value.newItem;
        newValue = true;
      } else if (old === null) {
        newValue = value.ports.length === 0;
      } else {
        newValue = old;
      }
      return newValue;
    });
    setProtocol(value.protocol || "");
    setMac(value.mac || "");
    setName(value.name || "");
    setPortNrs((old) => {
      const currentNrs = value.ports?.map((c) => c.nr) || [];
      if (isEqual(old, currentNrs)) {
        return old;
      }
      return currentNrs;
    });
  }, [value]);

  const dialog = useMemo(
    () => (
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle sx={(theme) => ({ backgroundColor: "secondary.main", color: "secondary.contrastText", fontSize: theme.typography.subtitle2.fontSize })}>
          {formatMessage({ defaultMessage: "Configure new IO port" })}
        </DialogTitle>
        <DialogContent sx={{ paddingBottom: 0, overflowX: "hidden" }}>
          <Typography>{formatMessage({ defaultMessage: "Select which IO port you want to configure." })}</Typography>

          {omittedNrs.length > 0 && (
            <>
              <Typography variant="body2" color="text.secondary">
                {formatMessage({ defaultMessage: "Recommended:" })}
              </Typography>
              <Grid container spacing={1} sx={{ marginX: 2, marginTop: 0, marginBottom: 1 }}>
                {omittedNrs.slice(0, 4).map((nr) => (
                  <Grid item key={`omitted-${nr}`}>
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={() => {
                        addNewPort(nr);
                        setDialogOpen(false);
                      }}
                    >
                      {nr}
                    </Button>
                  </Grid>
                ))}
              </Grid>
            </>
          )}
          <TextField
            autoFocus
            autoComplete="off"
            type="number"
            fullWidth
            variant="filled"
            size="small"
            color="secondary"
            label={formatMessage({ defaultMessage: "IO port number" })}
            inputProps={{ min: 1 }}
            value={newPortNr}
            error={!validNewPortNr}
            helperText={newPortNrError}
            onFocus={(event) => event.target.select()}
            onChange={(e) => handleNewPortNrChange(e.target.value, portNrs)}
            onKeyPress={(ev) => {
              if (ev.key === "Enter") {
                ev.preventDefault();
                if (newPortNr) {
                  addNewPort(parseInt(newPortNr, 10));
                  setDialogOpen(false);
                }
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} sx={{ color: "text.primary" }}>
            {formatMessage({ defaultMessage: "Cancel" })}
          </Button>
          <Button
            color="secondary"
            disabled={!validNewPortNr}
            onClick={() => {
              addNewPort(parseInt(newPortNr, 10));
              setDialogOpen(false);
            }}
          >
            {formatMessage({ defaultMessage: "Add" })}
          </Button>
        </DialogActions>
      </Dialog>
    ),
    [dialogOpen, formatMessage, omittedNrs, newPortNr, validNewPortNr, newPortNrError, addNewPort, handleNewPortNrChange, portNrs]
  );

  const protocols = useMemo(
    () => [
      { value: "C", name: formatMessage({ defaultMessage: "CAN" }) },
      { value: "L", name: formatMessage({ defaultMessage: "LoRa" }) },
    ],
    [formatMessage]
  );

  let editView = useMemo(() => {
    if (!value?.ports) {
      return;
    }
    return (
      <Grid container spacing={1} sx={{ padding: 1 }}>
        <Grid item container spacing={1} flex={1} alignItems="center">
          <Grid item xs={12} sm={6} md="auto">
            <TextField
              label={formatMessage({ defaultMessage: "Name" })}
              variant="filled"
              value={name}
              className={valueEdited(saved?.name, name, value.name) ? editedStyle.input : ""}
              onChange={(e) => {
                setName(e.target.value);
              }}
              onBlur={() => {
                value.name = name;
                onChange(boardIndex, value);
              }}
              sx={{ margin: 0, md: { width: 250, minWidth: 250 } }}
              fullWidth
              size="small"
              autoComplete="off"
              disabled={disabled || value.deleted}
              InputProps={saved && saved.name !== name ? { endAdornment: <ResetAdornment /> } : undefined}
              onContextMenu={saved && saved.name !== mac ? (e) => openContextMenu(e, `smartalarm.panels.${boardIndex}.name`) : undefined}
            />
          </Grid>
          {boardIndex > 0 && (
            <>
              <Grid item xs={12} sm={6} md="auto">
                <FormControl
                  variant="filled"
                  size="small"
                  sx={{ margin: 0 }}
                  className={saved?.protocol !== protocol ? editedStyle.input : ""}
                  disabled={disabled || value?.deleted}
                  fullWidth
                  onContextMenu={saved && saved.protocol !== protocol ? (e) => openContextMenu(e, `smartalarm.panels.${boardIndex}.protocol`) : undefined}
                >
                  <InputLabel id="protocol-select-label">{formatMessage({ defaultMessage: "Protocol" })}</InputLabel>
                  <Select
                    labelId="protocol-select-label"
                    value={protocol}
                    onChange={handleProtocolChange}
                    endAdornment={saved && saved.protocol !== protocol ? <ResetAdornment sx={{ mr: 2 }} /> : undefined}
                  >
                    {protocols?.map((protocol) => (
                      <MenuItem key={`protocol-${protocol.value}`} value={protocol.value}>
                        {protocol.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md="auto">
                <MaskedTextField
                  label={formatMessage({ defaultMessage: "MAC address" })}
                  mask="##-##-##-##-##-##"
                  definitions={{ "#": /[0-9a-fA-F]/ }}
                  placeholder="00-00-00-00-00-00"
                  value={mac}
                  className={saved?.mac !== mac ? editedStyle.input : ""}
                  onChange={setMac}
                  onBlur={() => {
                    value.mac = mac;
                    onChange(boardIndex, value);
                  }}
                  size="small"
                  sx={{ margin: 0 }}
                  autoComplete="off"
                  disabled={disabled || value.deleted}
                  fullWidth
                  InputProps={saved && saved.mac !== mac ? { endAdornment: <ResetAdornment /> } : undefined}
                  onContextMenu={saved && saved.mac !== mac ? (e) => openContextMenu(e, `smartalarm.panels.${boardIndex}.mac`) : undefined}
                />
              </Grid>
            </>
          )}
          {boardIndex > 0 && !value.deleted && !disabled && (
            <Grid item container flex={1} flexDirection="row" justifyContent="flex-end">
              <Grid item>
                <Button startIcon={<DeleteForeverOutlinedIcon />} color="error" onClick={onDelete} sx={{ alignSelf: "flex-end" }}>
                  {formatMessage({ defaultMessage: "Remove" })}
                </Button>
              </Grid>
            </Grid>
          )}
          {value.deleted && !disabled && (
            <Grid item sx={{ flex: 1, display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
              <Button color="inherit" onClick={onRestore}>
                {formatMessage({ defaultMessage: "Restore" })}
              </Button>
            </Grid>
          )}
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={1}>
            {portNrs.length === value.ports.length &&
              portNrs.map(
                (
                  nr: number,
                  index: number // portEditor(nr, index)
                ) => (
                  <Grid item key={`port-${nr}-${index}`} xs={12}>
                    <IOPort
                      nr={nr}
                      value={value.ports[index].value}
                      saved={saved?.ports?.find((port) => port.nr === value.ports[index]?.nr)?.value || null}
                      onChange={portValueChanged}
                      disabled={disabled || value.deleted}
                    />
                  </Grid>
                )
              )}
          </Grid>
        </Grid>
        <Grid item>
          <Button variant="text" startIcon={<AddIcon />} onClick={() => openNewPortDialog(portNrs)} disabled={disabled || value.deleted}>
            {formatMessage({ defaultMessage: "Configure new IO port" })}
          </Button>
        </Grid>
      </Grid>
    );
  }, [value, formatMessage, name, saved, editedStyle.input, disabled, mac, boardIndex, protocol, handleProtocolChange, protocols, onDelete, onRestore, portNrs, onChange, openContextMenu, portValueChanged, openNewPortDialog]);

  const cardColor = theme.palette.primary.main; //index === 0 ? theme.palette.primary.main : theme.palette.primary.light;

  return (
    <>
      <TitledCard
        color={value.deleted ? editedBackground : cardColor}
        contrastColor={value.deleted ? editedText : undefined}
        title={
          (value.deleted ? `${formatMessage({ defaultMessage: "deleted" }).toUpperCase()} | ` : "") +
          (name ||
            (mac && formatMac(mac)) ||
            (boardIndex === 0
              ? formatMessage({ defaultMessage: "Master panel" })
              : formatMessage({ defaultMessage: "Slave panel #{index}" }, { index: boardIndex })))
        }
        modes={[
          {
            icon: <RemoveRedEyeOutlinedIcon fontSize="small" />,
            content: <ZonesOverview value={value} saved={saved} />,
          },
          {
            icon:
              disabled || value.deleted ? (
                <Badge
                  badgeContent={<RemoveRedEyeOutlinedIcon fontSize="inherit" />}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                >
                  <EditOutlinedIcon fontSize="small" />
                </Badge>
              ) : (
                <EditOutlinedIcon fontSize="small" />
              ),
            content: editView,
          },
        ]}
        activeMode={newItem ? 1 : 0}
      />
      {dialog}
    </>
  );
};

export default SmartalarmBoard;
